/**
 * PATH: packages/email-hub/src/feature/components/rich-text/extensions.ts
 * FILE_ID: EMAILHUB-RICHTEXT-EXTENSIONS-001
 * PURPOSE: Assemble the TipTap extension set for the email body editor and
 *          expose the scoped display stylesheet. StarterKit v3 already
 *          bundles Link, Underline, lists, blockquote, headings, hr and
 *          undo/redo, so we only add what it lacks.
 */

import StarterKit from '@tiptap/starter-kit'
import { TextStyleKit } from '@tiptap/extension-text-style'
import { Highlight } from '@tiptap/extension-highlight'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TextAlign } from '@tiptap/extension-text-align'
import type { Extension, Range, Node as TNode, Mark } from '@tiptap/core'
import { ResizableImage } from './resizableImage'
import { buildTableExtensions } from './tableExtensions'
import { KanbanCardNode } from './kanbanCardNode'
import { QuoteBlockNode } from './quoteBlockNode'
import { SignatureBlockNode } from './signatureBlockNode'
import { buildGreetingSuggest } from './greetingSuggest'
import { buildMention, type MentionPerson } from './mention'
import { buildSlash, type SlashAction, type SlashItem } from './slash'
import type { SuggestionController } from './suggestionController'

export interface BuildExtensionsOptions {
  controller: SuggestionController
  placeholder: string
  searchContacts: (query: string, signal: AbortSignal) => Promise<MentionPerson[]>
  onMention: (person: MentionPerson) => void
  onSlashAction: (action: SlashAction, range: Range) => void
  /** Optional live getter for the full "/" item list (static blocks + saved
   *  snippets). When omitted, the built-in SLASH_ITEMS are used. */
  getSlashItems?: () => SlashItem[]
}

export function buildExtensions(opts: BuildExtensionsOptions): Array<Extension | TNode | Mark> {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      link: {
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      },
      // Placeholder ships separately below with our config.
      dropcursor: { color: 'rgba(99,102,241,0.5)', width: 2 },
    }),
    TextStyleKit,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ResizableImage,
    Placeholder.configure({
      placeholder: opts.placeholder,
      showOnlyWhenEditable: true,
      includeChildren: false,
    }),
    ...buildTableExtensions(),
    KanbanCardNode,
    QuoteBlockNode,
    SignatureBlockNode,
    buildMention(opts.controller, opts.searchContacts, opts.onMention),
    buildGreetingSuggest(opts.controller, opts.searchContacts),
    buildSlash(opts.controller, opts.onSlashAction, opts.getSlashItems),
  ] as Array<Extension | TNode | Mark>
}

/**
 * Scoped display styling for the ProseMirror editor. Tailwind v4 Preflight
 * flattens ul/ol/headings/blockquote and the tenants don't load
 * @tailwindcss/typography, so without these the formatting buttons mutate the
 * DOM but render no visible change. Scoped to `.qte-editor .ProseMirror`.
 */
export const QTE_EDITOR_CSS = `
.qte-editor .ProseMirror { outline: none; min-height: 240px; }
.qte-editor .ProseMirror h1 { font-size: 1.6em; font-weight: 700; line-height: 1.25; margin: 0.5em 0 0.35em; }
.qte-editor .ProseMirror h2 { font-size: 1.3em; font-weight: 700; line-height: 1.3; margin: 0.5em 0 0.3em; }
.qte-editor .ProseMirror h3 { font-size: 1.1em; font-weight: 600; line-height: 1.35; margin: 0.45em 0 0.25em; }
.qte-editor .ProseMirror ul { list-style: disc outside; margin: 0.5em 0; padding-left: 1.6em; }
.qte-editor .ProseMirror ol { list-style: decimal outside; margin: 0.5em 0; padding-left: 1.6em; }
.qte-editor .ProseMirror ul ul { list-style: circle outside; margin: 0.2em 0; }
.qte-editor .ProseMirror ul ul ul { list-style: square outside; }
.qte-editor .ProseMirror ul ul ul ul { list-style: disc outside; }
.qte-editor .ProseMirror ul ul ul ul ul { list-style: circle outside; }
.qte-editor .ProseMirror ol ol { list-style: lower-alpha outside; margin: 0.2em 0; }
.qte-editor .ProseMirror ol ol ol { list-style: lower-roman outside; }
.qte-editor .ProseMirror ol ol ol ol { list-style: upper-alpha outside; }
.qte-editor .ProseMirror ol ol ol ol ol { list-style: upper-roman outside; }
.qte-editor .ProseMirror li { margin: 0.15em 0; }
.qte-editor .ProseMirror li > p { margin: 0; }
.qte-editor .ProseMirror blockquote { border-left: 3px solid #cbd5e1; margin: 0.5em 0; padding-left: 0.9em; color: #475569; font-style: italic; }
.qte-editor .ProseMirror a { color: #2563eb; text-decoration: underline; }
.qte-editor .ProseMirror hr { border: none; border-top: 1px solid #cbd5e1; margin: 0.8em 0; }
.qte-editor .ProseMirror img.qte-img { max-width: 100%; height: auto; border-radius: 4px; }
.qte-editor .ProseMirror img.ProseMirror-selectednode { outline: 2px solid #6366f1; }
.qte-editor .ProseMirror table { border-collapse: collapse; margin: 8px 0; width: 100%; table-layout: fixed; overflow: hidden; }
.qte-editor .ProseMirror th, .qte-editor .ProseMirror td { border: 1px solid #d1d5db; padding: 6px 8px; vertical-align: top; position: relative; min-width: 2em; }
.qte-editor .ProseMirror th { background: #f3f4f6; font-weight: 600; text-align: left; }
.qte-editor .ProseMirror .selectedCell:after { content: ""; position: absolute; inset: 0; background: rgba(99,102,241,0.12); pointer-events: none; }
.qte-editor .ProseMirror .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: 0; width: 4px; background: #6366f1; cursor: col-resize; }
.qte-editor .ProseMirror .qte-mention { color: #2563eb; font-weight: 500; }
/* Tables INSIDE an opaque preview (quoted original / pasted signature) are
   the EMAIL's own layout tables - render them faithfully, not with the
   editor's editing-grid chrome (borders + min-width painted empty spacer
   cells as a glitchy grid: BUG-2026-07-07-EMAIL-QUOTE-TABLE-GRID). */
.qte-editor .ProseMirror .qte-quote-preview table,
.qte-editor .ProseMirror .qte-signature-preview table { width: auto; table-layout: auto; margin: 0; }
.qte-editor .ProseMirror .qte-quote-preview th, .qte-editor .ProseMirror .qte-quote-preview td,
.qte-editor .ProseMirror .qte-signature-preview th, .qte-editor .ProseMirror .qte-signature-preview td { border: none; padding: 0; min-width: 0; background: transparent; font-weight: inherit; }
.qte-editor .ProseMirror .qte-quote-preview img,
.qte-editor .ProseMirror .qte-signature-preview img { max-width: 100%; height: auto; }
.qte-editor .ProseMirror .qte-quote-preview { border-left: 3px solid #cbd5e1; padding-left: 12px; color: #475569; font-size: 0.9em; }
.qte-editor .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #9ca3af; pointer-events: none; height: 0; }
`
